import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { X, Megaphone, ThumbsUp, Map, CheckCircle, Circle, MessageSquare } from 'lucide-react';

const CommunityModal = ({ onClose, initialTab = 'updates' }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab); // 'updates', 'roadmap', 'voting', 'feedback'
  const [updates, setUpdates] = useState([]);
  const [roadmap, setRoadmap] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newRequest, setNewRequest] = useState({ title: '', description: '' });
  const [feedback, setFeedback] = useState({ category: 'bug', title: '', description: '' });
  const [feedbackSent, setFeedbackSent] = useState(false);

  useEffect(() => {
    if (activeTab === 'voting') fetchRequests();
    if (activeTab === 'updates') fetchUpdates();
    if (activeTab === 'roadmap') fetchRoadmap();
    if (activeTab === 'feedback') { setFeedbackSent(false); }
  }, [activeTab]);

  const fetchUpdates = async () => {
    setLoading(true);
    const { data } = await supabase.from('app_updates').select('*').order('created_at', { ascending: false });
    setUpdates(data || []);
    setLoading(false);
  };

  const fetchRoadmap = async () => {
    setLoading(true);
    const { data } = await supabase.from('roadmap').select('*').order('display_order', { ascending: true });
    
    const grouped = (data || []).reduce((acc, item) => {
      const existing = acc.find(p => p.phase === item.phase);
      if (existing) {
        existing.items.push({ task: item.task, status: item.status });
      } else {
        acc.push({ phase: item.phase, items: [{ task: item.task, status: item.status }] });
      }
      return acc;
    }, []);

    setRoadmap(grouped);
    setLoading(false);
  };

  const fetchRequests = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('feature_requests')
      .select('*')
      .eq('category', 'feature')
      .order('vote_count', { ascending: false });
    setRequests(data || []);
    setLoading(false);
  };

  const handleVote = async (requestId) => {
    if (!user) return alert('Please sign in to vote!');
    const { error } = await supabase.from('feature_votes').insert([{ user_id: user.id, feature_id: requestId }]);
    if (error) {
      if (error.code === '23505') alert('You already voted for this!');
      else alert('Error voting: ' + error.message);
    } else {
      fetchRequests();
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!user) return alert('Please sign in to suggest features!');
    if (!newRequest.title) return;

    setIsSubmitting(true);
    const { error } = await supabase.from('feature_requests').insert([{
      user_id: user.id,
      title: newRequest.title,
      description: newRequest.description,
      category: 'feature'
    }]);

    if (!error) {
      setNewRequest({ title: '', description: '' });
      fetchRequests();
    } else {
      alert(error.message);
    }
    setIsSubmitting(false);
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (!user) return alert('Please sign in to leave feedback.');
    if (!feedback.title.trim()) return;

    setIsSubmitting(true);
    const { error } = await supabase.from('feature_requests').insert([{
      user_id: user.id,
      title: feedback.title,
      description: feedback.description,
      category: feedback.category,
      status: 'open',
    }]);

    if (!error) {
      setFeedback({ category: 'bug', title: '', description: '' });
      setFeedbackSent(true);
    } else {
      alert(error.message);
    }
    setIsSubmitting(false);
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Megaphone size={20} color="var(--accent)" />
            <h3 style={{ margin: 0 }}>Community Hub</h3>
          </div>
          <button onClick={onClose} style={closeButtonStyle}><X size={20} /></button>
        </div>

        {/* Tabs */}
        <div style={tabContainer}>
          <button style={activeTab === 'updates' ? activeTabStyle : tabStyle} onClick={() => setActiveTab('updates')}>
            What's New
          </button>
          <button style={activeTab === 'roadmap' ? activeTabStyle : tabStyle} onClick={() => setActiveTab('roadmap')}>
            Roadmap
          </button>
          <button style={activeTab === 'voting' ? activeTabStyle : tabStyle} onClick={() => setActiveTab('voting')}>
            Vote
          </button>
          <button style={activeTab === 'feedback' ? activeTabStyle : tabStyle} onClick={() => setActiveTab('feedback')}>
            Feedback
          </button>
        </div>

        <div style={contentStyle}>
          {activeTab === 'updates' && (
            <div style={listStyle}>
              {loading ? <p style={msg}>Gathering updates...</p> : updates.map(update => (
                <div key={update.id} style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={versionStyle}>{update.version}</span>
                    <span style={dateStyle}>{new Date(update.created_at).toLocaleDateString()}</span>
                  </div>
                  <h4 style={{ margin: '0 0 8px 0' }}>{update.title}</h4>
                  <p style={contentTextStyle}>{update.content}</p>
                </div>
              ))}
              {!loading && updates.length === 0 && <p style={msg}>No updates yet.</p>}
            </div>
          )}

          {activeTab === 'roadmap' && (
            <div style={listStyle}>
              {loading ? <p style={msg}>Plotting the course...</p> : roadmap.map((phase, idx) => (
                <div key={idx} style={cardStyle}>
                  <h4 style={{ margin: '0 0 12px 0', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    {phase.phase}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {phase.items.map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' }}>
                        {item.status === 'done' ? (
                          <CheckCircle size={16} color="#4caf50" />
                        ) : item.status === 'in-progress' ? (
                          <Map size={16} color="#2196f3" />
                        ) : (
                          <Circle size={16} color="#ccc" />
                        )}
                        <span style={{ 
                          color: item.status === 'done' ? 'var(--text-main)' : (item.status === 'in-progress' ? '#2196f3' : 'var(--text-soft)'),
                          textDecoration: item.status === 'done' ? 'line-through' : 'none'
                        }}>
                          {item.task}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {!loading && roadmap.length === 0 && <p style={msg}>The roadmap is being drafted.</p>}
            </div>
          )}

          {activeTab === 'voting' && (
            <div style={listStyle}>
              {loading ? <p>Loading...</p> : (
                <>
                  <div style={{ marginBottom: '1rem', padding: '12px', backgroundColor: 'var(--bg-app)', borderRadius: '8px', fontSize: '0.9rem' }}>
                    <h5 style={{ margin: '0 0 4px 0' }}>Request a Feature</h5>
                    <form onSubmit={handleSubmitRequest} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <input 
                        style={inputStyle}
                        placeholder="Feature Title"
                        value={newRequest.title}
                        onChange={e => setNewRequest({...newRequest, title: e.target.value})}
                      />
                      <button type="submit" style={miniButtonStyle} disabled={isSubmitting}>
                        {isSubmitting ? 'Sending...' : 'Submit'}
                      </button>
                    </form>
                  </div>
                  
                  {requests.map(req => (
                    <div key={req.id} style={requestCardStyle}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 4px 0' }}>{req.title}</h4>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-soft)', margin: 0 }}>{req.description}</p>
                      </div>
                      <button onClick={() => handleVote(req.id)} style={voteButtonStyle}>
                        <ThumbsUp size={16} />
                        <span>{req.vote_count}</span>
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {activeTab === 'feedback' && (
            <div style={listStyle}>
              <div style={{ ...cardStyle, backgroundColor: 'var(--bg-app)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <MessageSquare size={16} color="var(--accent)" />
                  <h5 style={{ margin: 0, color: 'var(--text-main)' }}>Share Your Thoughts</h5>
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-soft)', margin: '0 0 12px 0' }}>
                  Bug, idea, or just want to say something? We read every submission.
                </p>
                {feedbackSent ? (
                  <div style={{ textAlign: 'center', padding: '16px' }}>
                    <CheckCircle size={32} color="#4caf50" style={{ marginBottom: '8px' }} />
                    <p style={{ margin: 0, fontWeight: '600', color: 'var(--text-main)' }}>Thank you! 🌿</p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: 'var(--text-soft)' }}>Your feedback has been received.</p>
                    <button style={{ ...miniButtonStyle, marginTop: '12px' }} onClick={() => setFeedbackSent(false)}>
                      Send Another
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitFeedback} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {[
                        { value: 'bug', label: '🐛 Bug' },
                        { value: 'feature', label: '✨ Feature' },
                        { value: 'general', label: '💬 General' },
                        { value: 'complaint', label: '⚠️ Complaint' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setFeedback(f => ({ ...f, category: opt.value }))}
                          style={{
                            flex: 1,
                            padding: '6px 4px',
                            borderRadius: '8px',
                            border: `1px solid ${feedback.category === opt.value ? 'var(--accent)' : 'var(--border)'}`,
                            backgroundColor: feedback.category === opt.value ? 'var(--accent-soft)' : 'transparent',
                            color: feedback.category === opt.value ? 'var(--accent)' : 'var(--text-soft)',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <input
                      style={inputStyle}
                      placeholder={feedback.category === 'bug' ? "What's broken?" : feedback.category === 'feature' ? "What would you like?" : "What's on your mind?"}
                      value={feedback.title}
                      onChange={e => setFeedback(f => ({ ...f, title: e.target.value }))}
                      required
                    />
                    <textarea
                      style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
                      placeholder="Any extra details..."
                      value={feedback.description}
                      onChange={e => setFeedback(f => ({ ...f, description: e.target.value }))}
                    />
                    <button type="submit" style={miniButtonStyle} disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send Feedback'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// Styles
const overlayStyle = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
  padding: '1rem'
};

const modalStyle = {
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '1.5rem',
  width: '100%',
  maxWidth: '450px',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem'
};

const closeButtonStyle = { background: 'none', border: 'none', cursor: 'pointer', padding: '4px' };
const tabContainer = { display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '1rem' };
const tabStyle = { flex: 1, padding: '10px', background: 'none', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', color: 'var(--text-soft)', fontWeight: '500', fontSize: '0.8rem' };
const activeTabStyle = { ...tabStyle, color: 'var(--text-main)', borderBottom: '2px solid var(--accent)' };
const contentStyle = { flex: 1, overflowY: 'auto' };
const listStyle = { display: 'flex', flexDirection: 'column', gap: '12px' };

const cardStyle = { padding: '12px', backgroundColor: 'var(--bg-app)', borderRadius: '12px', border: '1px solid var(--border)' };
const requestCardStyle = { ...cardStyle, display: 'flex', alignItems: 'center', gap: '12px' };

const versionStyle = { fontSize: '0.7rem', fontWeight: 'bold', color: 'var(--text-main)', backgroundColor: 'var(--border)', padding: '2px 6px', borderRadius: '4px' };
const dateStyle = { fontSize: '0.7rem', color: 'var(--text-soft)' };
const contentTextStyle = { fontSize: '0.9rem', color: 'var(--text-main)', margin: 0, lineHeight: '1.4' };

const voteButtonStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  background: 'none',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  padding: '8px',
  cursor: 'pointer',
  minWidth: '45px',
  transition: 'all 0.2s',
  backgroundColor: 'var(--bg-card)',
  color: 'var(--text-main)'
};

const inputStyle = { width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '0.9rem', boxSizing: 'border-box', backgroundColor: 'var(--bg-card)', color: 'var(--text-main)' };
const miniButtonStyle = { padding: '6px 12px', backgroundColor: 'var(--accent)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', width: 'fit-content' };
const msg = { textAlign: 'center', padding: '20px', color: 'var(--text-soft)', fontSize: '0.9rem' };

export default CommunityModal;
